import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { isNative } from '../utils/native';

// Moved out of Settings.js so Billing has its own place in the main menu,
// instead of being tucked away inside Settings.
//
// NAMES: the `key` values below ('collector', 'enthusiast', 'curator') are the
// strings stored in user_profiles.plan and matched by the Stripe webhook — they
// are NOT renamed, to avoid breaking plan detection for existing subscribers.
// Only `name` (the display label) was wrong before and is now fixed to match
// the landing page: key 'collector' = the entry-level tier, DISPLAYED as
// "Enthusiast"; key 'enthusiast' = the mid tier, DISPLAYED as "Collector".
//
// ⚠️ PRICES: `price` below is now €99 / €149 to match the landing page, but this
// is DISPLAY TEXT ONLY. The amount actually charged comes from the Stripe
// `priceId` on each plan, which still points at the OLD €49 / €99 Stripe Price
// objects (see create-checkout-session/index.ts + its STRIPE_*_PRICE secrets).
// Until you create new Stripe Prices at €99/€149 and swap the secrets to match
// (steps were in the earlier report), a customer will see €99 here but
// actually be charged €49. Do that Stripe step before relying on this price.
export const PLANS = [
  {
    key: 'free',
    name: 'Free',
    tagline: 'For getting started with your collection.',
    price: '€0',
    period: '/mo',
    color: '#6b7280',
    grad: ['#6b7280','#4b5563'],
    features: ['2 vehicles', 'Basic fleet management', 'Alerts & documents', 'No AI features'],
  },
  {
    key: 'collector',
    name: 'Enthusiast',
    tagline: 'For smaller or growing collections.',
    price: '€19',
    period: '/mo',
    color: '#4f8ef7',
    grad: ['#4f8ef7','#7c3aed'],
    features: ['Up to 5 vehicles', 'Full collection management', 'Insurance & road tax alerts', 'Document vault', 'Basic AI valuation', '1 user'],
    priceId: 'price_1Tb1dI1Lgciwxa58wFRc8Dj4',
  },
  {
    key: 'enthusiast',
    name: 'Collector',
    tagline: 'For serious collectors.',
    price: '€99',
    period: '/mo',
    color: '#a855f7',
    grad: ['#a855f7','#ec4899'],
    features: ['Up to 15 vehicles', 'Everything in Enthusiast', 'Full Car Intelligence', 'Full market comparables database', 'Up to 3 users', 'Email alert notifications'],
    priceId: 'price_1Tb1eZ1Lgciwxa58u9teoWog',
    popular: true,
    onboardingFee: true,
  },
  {
    key: 'curator',
    name: 'Curator',
    tagline: 'For larger or advanced collections.',
    price: '€149',
    period: '/mo',
    color: '#c9a84c',
    grad: ['#c9a84c','#e8c97a'],
    features: ['Unlimited vehicles', 'Everything in Collector', 'AI market intelligence reports', 'Unlimited team members', 'Priority support'],
    priceId: 'price_1Tb1fI1Lgciwxa587Q4Rx8oi',
    onboardingFee: true,
  },
];

export const BillingSection = ({ user }) => {
  const [profile, setProfile]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [upgrading, setUpgrading] = useState('');

  useEffect(() => {
    if (!user) return;
    supabase.from('user_profiles').select('plan,trial_ends_at,subscription_status,stripe_customer_id').eq('id', user.id).single().then(({ data }) => {
      setProfile(data);
      setLoading(false);
    });
  }, [user]);

  // Check for success/cancelled query params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      const plan = params.get('plan');
      toast.success(`Successfully upgraded to ${plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : 'new plan'}!`);
      window.history.replaceState({}, '', window.location.pathname);
      supabase.from('user_profiles').select('plan,trial_ends_at,subscription_status').eq('id', user.id).single().then(({ data }) => setProfile(data));
    }
    if (params.get('cancelled') === 'true') {
      toast.error('Payment cancelled.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [openingPortal, setOpeningPortal] = useState(false);

  // Stripe's hosted portal: cancel, change plan, update card, download invoices.
  // Not a bespoke cancel button — Stripe handles proration, dunning and the
  // required cancellation wording, and a customer must be able to cancel as
  // easily as they subscribed.
  const openBillingPortal = async () => {
    setOpeningPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session');
      if (error || data?.error) {
        toast.error(data?.error || error.message || 'Could not open billing portal');
        setOpeningPortal(false);
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      toast.error('Could not open billing portal: ' + err.message);
      setOpeningPortal(false);
    }
  };

  const handleUpgrade = async (plan) => {
    if (!user) return;
    setUpgrading(plan.key);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          plan: plan.key,
          userId: user.id,
          userEmail: user.email,
          returnUrl: `${window.location.origin}/billing`,
        },
      });
      if (error || !data?.url) { toast.error('Failed to start checkout. Please try again.'); setUpgrading(''); return; }
      window.location.href = data.url;
    } catch (err) {
      toast.error('Error: ' + err.message);
      setUpgrading('');
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner"/></div>;

  const currentPlan   = profile?.plan || 'free';
  const trialEndsAt   = profile?.trial_ends_at ? new Date(profile.trial_ends_at) : null;
  const isTrialing    = profile?.subscription_status === 'trialing';
  const trialDaysLeft = trialEndsAt ? Math.max(0, Math.ceil((trialEndsAt - new Date()) / (1000 * 60 * 60 * 24))) : 0;
  const currentPlanData = PLANS.find(p => p.key === currentPlan) || PLANS[0];

  // Single status pill instead of separate emoji lines — matches the plain
  // badge language used on the plan cards below (no emoji anywhere there).
  const status = isTrialing && trialDaysLeft > 0
    ? { text: `Free trial — ${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''} remaining`, color: '#eab308' }
    : isTrialing && trialDaysLeft === 0
    ? { text: 'Trial expired — upgrade to continue', color: '#ef4444' }
    : currentPlan !== 'free'
    ? { text: 'Active subscription', color: currentPlanData.grad[0] }
    : { text: 'Upgrade to unlock all features', color: 'var(--text3)' };

  // Inside the iPhone / Android app there is no plan selection or checkout (store rules):
  // it only shows which plan the account is on. Subscriptions are managed outside the app.
  if (isNative) {
    return (
      <div style={{ maxWidth: 520, margin: '0 auto' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10, fontFamily: 'DM Mono, monospace' }}>Your account</div>
        <div className="card" style={{ padding: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 6, fontFamily: 'DM Mono, monospace', textTransform: 'uppercase', letterSpacing: 1 }}>Current Plan</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: currentPlanData.grad[0], marginBottom: 12 }}>{currentPlanData.name}</div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 600, color: status.color, border: `1px solid ${status.color}55`, borderRadius: 999, padding: '5px 12px' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: status.color }} />
            {isTrialing && trialDaysLeft > 0 ? status.text : currentPlan !== 'free' ? 'Active subscription' : 'Free plan'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 18, lineHeight: 1.6 }}>
            Your plan features are available in this app. Account and subscription management is not available inside the app.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <div style={{ textAlign: 'center', maxWidth: 580, margin: '0 auto 32px' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10, fontFamily: 'DM Mono, monospace' }}>
          Manage Your Subscription
        </div>
        <div style={{ fontSize: 30, fontWeight: 800, color: 'var(--text)', letterSpacing: -0.5, lineHeight: 1.25 }}>
          Choose the right plan for your collection
        </div>
      </div>

      {/* Current plan banner — same glass-card language as the pricing cards below */}
      <div style={{
        background: `linear-gradient(160deg, ${currentPlanData.grad[0]}1c, ${currentPlanData.grad[1]}12 55%, var(--surface) 100%)`,
        border: `1px solid ${currentPlanData.grad[0]}4d`, borderRadius: 22, padding: '26px 28px', marginBottom: 40,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16,
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        boxShadow: `0 24px 48px -28px ${currentPlanData.grad[0]}66`,
      }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 6, fontFamily: 'DM Mono, monospace', textTransform: 'uppercase', letterSpacing: 1 }}>Current Plan</div>
          <div style={{ fontSize: 26, fontWeight: 800, background: `linear-gradient(135deg, ${currentPlanData.grad[0]}, ${currentPlanData.grad[1]})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', marginBottom: 10 }}>
            {currentPlanData.name}
          </div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 600,
            color: status.color, background: `${status.color}18`, border: `1px solid ${status.color}33`,
            borderRadius: 999, padding: '5px 12px',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: status.color, flexShrink: 0 }} />
            {status.text}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 30, fontWeight: 900, color: 'var(--text)' }}>{currentPlanData.price}<span style={{ fontSize: 14, color: 'var(--text3)', fontWeight: 500 }}>{currentPlanData.period}</span></div>
          {profile?.stripe_customer_id && (
            <>
              <button
                onClick={openBillingPortal} disabled={openingPortal}
                style={{ marginTop: 12, padding: '9px 18px', borderRadius: 999, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', fontSize: 13, fontWeight: 600, cursor: openingPortal ? 'not-allowed' : 'pointer', fontFamily: 'DM Sans, sans-serif' }}
              >
                {openingPortal ? 'Opening…' : 'Manage subscription'}
              </button>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8, maxWidth: 210 }}>
                Cancel, change plan, update your card or download invoices.
              </div>
            </>
          )}
        </div>
      </div>

      {/* Plan cards — Free isn't sold here, only shown as the fallback state
          above when that's what the account is actually on. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24, marginBottom: 32, alignItems: 'start', paddingTop: 22 }}>
        {PLANS.filter(plan => plan.key !== 'free').map(plan => {
          const isCurrent = currentPlan === plan.key;
          const isUpgrade = !isCurrent && plan.key !== 'free';
          const showBadge = plan.popular && !isCurrent;
          return (
            <div key={plan.key} style={{ position: 'relative' }}>
              {/* Floating badge — straddles the card's top edge */}
              {showBadge && (
                <div style={{
                  position: 'absolute', top: 0, left: '50%', transform: 'translate(-50%, -50%)',
                  zIndex: 2, whiteSpace: 'nowrap', fontSize: 12, fontWeight: 600, padding: '7px 16px',
                  borderRadius: 999, background: `linear-gradient(135deg, ${plan.grad[0]}, ${plan.grad[1]})`,
                  color: 'white', boxShadow: `0 6px 18px ${plan.grad[0]}55`,
                }}>
                  Best value for money!
                </div>
              )}
              {isCurrent && (
                <div style={{
                  position: 'absolute', top: 0, left: '50%', transform: 'translate(-50%, -50%)',
                  zIndex: 2, whiteSpace: 'nowrap', fontSize: 11, fontWeight: 600, padding: '6px 14px',
                  borderRadius: 999, background: `${plan.grad[0]}22`, color: plan.grad[0],
                  border: `1px solid ${plan.grad[0]}55`, backdropFilter: 'blur(8px)',
                }}>
                  ✓ Current plan
                </div>
              )}

              <div style={{
                background: showBadge || isCurrent
                  ? `linear-gradient(160deg, ${plan.grad[0]}1c, ${plan.grad[1]}12 55%, var(--surface) 100%)`
                  : 'var(--surface)',
                border: `1px solid ${showBadge || isCurrent ? plan.grad[0] + '4d' : 'var(--border)'}`,
                borderRadius: 22, padding: '28px 24px', height: '100%',
                display: 'flex', flexDirection: 'column',
                backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                boxShadow: showBadge ? `0 24px 48px -28px ${plan.grad[0]}88` : 'none',
                transition: 'all 0.2s',
              }}>
                <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>{plan.name}</div>
                <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 18, lineHeight: 1.4 }}>{plan.tagline}</div>

                <div style={{ fontSize: 38, fontWeight: 900, background: `linear-gradient(135deg, ${plan.grad[0]}, ${plan.grad[1]})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', marginBottom: plan.onboardingFee ? 4 : 22 }}>
                  {plan.price}<span style={{ fontSize: 14, fontWeight: 500, WebkitTextFillColor: 'var(--text3)' }}>{plan.period}</span>
                </div>
                {plan.onboardingFee && (
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 22, lineHeight: 1.4 }}>
                    + €150 one-time onboarding per car · free for the first 100 users
                  </div>
                )}

                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
                  What You Get
                </div>
                <ul style={{ listStyle: 'none', marginBottom: 24, display: 'flex', flexDirection: 'column', gap: 9, flex: 1 }}>
                  {plan.features.map(f => (
                    <li key={f} style={{ fontSize: 13.5, color: 'var(--text2)', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <Check size={14} style={{ color: plan.grad[0], flexShrink: 0, marginTop: 2 }} />
                      {f}
                    </li>
                  ))}
                </ul>

                <div style={{ marginTop: 'auto' }}>
                  {isUpgrade && (
                    <button
                      onClick={() => handleUpgrade(plan)}
                      disabled={!!upgrading}
                      style={{
                        width: '100%', padding: '12px', borderRadius: 999,
                        background: showBadge ? `linear-gradient(135deg, ${plan.grad[0]}, ${plan.grad[1]})` : 'var(--surface2)',
                        color: showBadge ? 'white' : 'var(--text)',
                        boxShadow: showBadge ? `0 4px 14px ${plan.grad[0]}44` : 'none',
                        border: showBadge ? 'none' : '1px solid var(--border)',
                        fontSize: 13.5, fontWeight: 600, cursor: upgrading ? 'not-allowed' : 'pointer',
                        opacity: upgrading && upgrading !== plan.key ? 0.5 : 1,
                        fontFamily: 'DM Sans, sans-serif', transition: 'all 0.2s',
                      }}
                    >
                      {upgrading === plan.key ? 'Redirecting…' : 'Subscribe'}
                    </button>
                  )}
                  {isCurrent && (
                    <div style={{ width: '100%', padding: '12px', borderRadius: 999, border: `1px solid ${plan.grad[0]}33`, color: plan.grad[0], fontSize: 13.5, fontWeight: 600, textAlign: 'center', background: `${plan.grad[0]}0c` }}>
                      Your current plan
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Billing note */}
      <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--text3)', lineHeight: 1.6 }}>
        💳 Payments are processed securely by <strong style={{ color: 'var(--text2)' }}>Stripe</strong>. You can cancel anytime.
        For billing support contact <strong style={{ color: 'var(--text2)' }}>support@carcollectorstudio.com</strong>.
      </div>
    </div>
  );
};

export const Billing = () => {
  const { user } = useAuth();
  return (
    <div className="page">
      <BillingSection user={user} />
    </div>
  );
};
